import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

// Axios interceptor - token'ı otomatik olarak header'a ekler
const getAuthConfig = () => {
  const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  return {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  };
};

// Tüm toplantıları getir (Admin)
export const getMeetings = createAsyncThunk(
  "meetings/getMeetings",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/meetings`, getAuthConfig());
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Error getting meetings" }
      );
    }
  }
);

// ID'ye göre toplantı getir
export const getMeetingById = createAsyncThunk(
  "meetings/getMeetingById",
  async (meetingId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/meetings/${meetingId}`, getAuthConfig());
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Error getting meeting by id" }
      );
    }
  }
);

// Yeni toplantı oluştur
export const createMeeting = createAsyncThunk(
  "meetings/createMeeting",
  async (meetingData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_URL}/meetings`, meetingData, getAuthConfig());
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Error creating meeting" }
      );
    }
  }
);

// Toplantı güncelle (Admin)
export const updateMeeting = createAsyncThunk(
  "meetings/updateMeeting",
  async ({ meetingId, updateData }, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `${API_URL}/meetings/${meetingId}`, 
        updateData, 
        getAuthConfig()
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Error updating meeting" }
      );
    }
  }
);

// Toplantı sil (Admin)
export const deleteMeeting = createAsyncThunk(
  "meetings/deleteMeeting",
  async (meetingId, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`${API_URL}/meetings/${meetingId}`, getAuthConfig());
      return { deletedMeeting: response.data, meetingId };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Error deleting meeting" }
      );
    }
  }
);

// Toplantı başlat
export const startMeeting = createAsyncThunk(
  "meetings/startMeeting",
  async (meetingId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_URL}/meetings/${meetingId}/start`, 
        getAuthConfig()
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Error starting meeting" }
      );
    }
  }
);

const meetingSlice = createSlice({
  name: "meetings",
  initialState: {
    meetings: [],
    currentMeeting: null,
    meetingUrl: null,
    loading: false,
    error: null,
    createLoading: false,
    updateLoading: false,
    deleteLoading: false,
    startLoading: false,
    success: false,
    successMessage: null
  },
  reducers: {
    // Hata mesajını temizle
    clearError: (state) => {
      state.error = null;
    },
    // Başarı mesajını temizle
    clearSuccess: (state) => {
      state.success = false;
      state.successMessage = null;
    },
    // Mevcut toplantıyı temizle
    clearCurrentMeeting: (state) => {
      state.currentMeeting = null;
      state.meetingUrl = null;
    },
    // Toplantı URL'sini temizle
    clearMeetingUrl: (state) => {
      state.meetingUrl = null;
    },
    // Loading state'lerini sıfırla
    resetLoadingStates: (state) => {
      state.loading = false;
      state.createLoading = false;
      state.updateLoading = false;
      state.deleteLoading = false;
      state.startLoading = false;
    }
  },
  extraReducers: (builder) => {
    builder
      // Tüm toplantıları getir
      .addCase(getMeetings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMeetings.fulfilled, (state, action) => {
        state.loading = false;
        state.meetings = action.payload;
        state.error = null;
      })
      .addCase(getMeetings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ID'ye göre toplantı getir
      .addCase(getMeetingById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMeetingById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentMeeting = action.payload;
        state.error = null;
      })
      .addCase(getMeetingById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Yeni toplantı oluştur
      .addCase(createMeeting.pending, (state) => {
        state.createLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createMeeting.fulfilled, (state, action) => {
        state.createLoading = false;
        state.meetings.push(action.payload);
        state.currentMeeting = action.payload;
        state.success = true;
        state.successMessage = "Meeting created successfully";
        state.error = null;
      })
      .addCase(createMeeting.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload;
        state.success = false;
      })

      // Toplantı güncelle
      .addCase(updateMeeting.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateMeeting.fulfilled, (state, action) => {
        state.updateLoading = false;
        const index = state.meetings.findIndex(
          meeting => meeting._id === action.payload._id
        );
        if (index !== -1) {
          state.meetings[index] = action.payload;
        }
        if (state.currentMeeting && state.currentMeeting._id === action.payload._id) {
          state.currentMeeting = action.payload;
        }
        state.success = true;
        state.successMessage = "Meeting updated successfully";
        state.error = null;
      })
      .addCase(updateMeeting.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
        state.success = false;
      })

      // Toplantı sil
      .addCase(deleteMeeting.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteMeeting.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.meetings = state.meetings.filter(
          meeting => meeting._id !== action.payload.meetingId
        );
        if (state.currentMeeting && state.currentMeeting._id === action.payload.meetingId) {
          state.currentMeeting = null;
        }
        state.success = true;
        state.successMessage = "Meeting deleted successfully";
        state.error = null;
      })
      .addCase(deleteMeeting.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
        state.success = false;
      })

      // Toplantı başlat
      .addCase(startMeeting.pending, (state) => {
        state.startLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(startMeeting.fulfilled, (state, action) => {
        state.startLoading = false;
        state.meetingUrl = action.payload.meetingUrl;
        state.success = true;
        state.successMessage = "Meeting started successfully";
        state.error = null;
      })
      .addCase(startMeeting.rejected, (state, action) => {
        state.startLoading = false;
        state.error = action.payload;
        state.success = false;
      });
  },
});

// Actions'ları export et
export const {
  clearError,
  clearSuccess,
  clearCurrentMeeting,
  clearMeetingUrl,
  resetLoadingStates
} = meetingSlice.actions;

// Selectors
export const selectMeetings = (state) => state.meetings.meetings;
export const selectCurrentMeeting = (state) => state.meetings.currentMeeting;
export const selectMeetingUrl = (state) => state.meetings.meetingUrl;
export const selectMeetingLoading = (state) => state.meetings.loading;
export const selectMeetingError = (state) => state.meetings.error;
export const selectMeetingSuccess = (state) => state.meetings.success;
export const selectCreateLoading = (state) => state.meetings.createLoading;
export const selectUpdateLoading = (state) => state.meetings.updateLoading;
export const selectDeleteLoading = (state) => state.meetings.deleteLoading;
export const selectStartLoading = (state) => state.meetings.startLoading;

export default meetingSlice.reducer;